/**
 * PowerReportingPage Component
 * Main page for power reporting management with Active/Recent/Batches tabs
 * Uses Oath authentication and OathUI components for consistent Tailwind styling
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  DocumentTextIcon,
  PlusIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../../services/oathAuthContext';
import {
  getActiveReports,
  getRecentReports,
  getBatchesWithAgencies,
  DEFAULT_PAGE_SIZE
} from '../../../services/oathPowerReportService';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathDualSpinner,
  OathEmptyState
} from '../../shared/OathUI';
import ActiveReportsTab from './components/ActiveReportsTab';
import RecentReportsTab from './components/RecentReportsTab';
import BatchesToPayTab from './components/BatchesToPayTab';
import { getPaymentPlans } from '../../../services/oathPaymentPlanService';
import usePermissions from '../../../hooks/usePermissions';
import { withErrorBoundary } from './components/shared';
import CreateReportModal from './modals/CreateReportModal';
import BatchPaymentModal from './modals/BatchPaymentModal';
import PdfViewerModal from './modals/PdfViewerModal';
import { getBatchId } from './utils';
import { getTodayForInput, getDateMonthsAgoForInput } from './utils/formatters';

// Default Recent Reports window - last 3 months, shown as the active preset in
// the date filter so agents can see the window and widen it (up to All time)
const getDefaultRecentDateRange = () => ({
  fromDate: getDateMonthsAgoForInput(3),
  toDate: getTodayForInput()
});

// Tab persistence via ?tab=<name>. Index 0 (Active) is the default and is
// represented by the absence of the query param to keep URLs clean.
const TAB_PARAM = 'tab';
const TAB_NAMES = ['active', 'recent', 'batches'];
const tabIndexFromName = (name) => {
  const i = TAB_NAMES.indexOf(name);
  return i >= 0 ? i : 0;
};

const PowerReportingPage = () => {
  const { user, isLoading: authLoading } = useOathAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Agency selection for multi-agency users (memoized to avoid dependency changes)
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);

  // Data state (local, not using context)
  const [activeReports, setActiveReports] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [batchesToPay, setBatchesToPay] = useState([]);
  // Agency payment plans — feed the plan-detail panels expanded from PaymentPlan batch
  // rows in the Batches to Pay tab. The tab doesn't block on this request, so it has no
  // loading flag; the expander simply appears once the plan data is in.
  const [paymentPlans, setPaymentPlans] = useState([]);

  // Loading states
  const [isLoadingActive, setIsLoadingActive] = useState(true);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);
  const [isLoadingBatches, setIsLoadingBatches] = useState(true);

  // Tab state — initial value comes from ?tab=<name> so refreshing the page
  // (or sharing a link) lands on the same tab the user was on.
  const [activeTab, setActiveTab] = useState(() => tabIndexFromName(searchParams.get(TAB_PARAM)));
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Batch payment modal state
  const [batchPaymentModalOpen, setBatchPaymentModalOpen] = useState(false);
  const [selectedBatchesForPayment, setSelectedBatchesForPayment] = useState(new Set());
  const [paymentAmountsForPayment, setPaymentAmountsForPayment] = useState({});
  const [batchesTabKey, setBatchesTabKey] = useState(0);

  // Agency filter state for Batches to Pay tab
  const [agenciesWithBatches, setAgenciesWithBatches] = useState([]);
  const [batchAgencyFilter, setBatchAgencyFilter] = useState(null);
  const [isLoadingAgenciesWithBatches, setIsLoadingAgenciesWithBatches] = useState(false);
  const [batchesDataLoaded, setBatchesDataLoaded] = useState(false); // Track if batches have been loaded

  // Derive correct agencyId from selected batches for batch payment modal
  const batchPaymentAgencyId = useMemo(() => {
    if (batchAgencyFilter) return batchAgencyFilter;
    if (selectedBatchesForPayment.size > 0 && batchesToPay.length > 0) {
      for (const batchId of selectedBatchesForPayment) {
        const batch = batchesToPay.find(b => getBatchId(b) === batchId);
        if (batch?.agencyId) return batch.agencyId;
      }
    }
    return selectedAgencyId;
  }, [batchAgencyFilter, selectedBatchesForPayment, batchesToPay, selectedAgencyId]);

  // Refresh trigger
  const [refreshToggle, setRefreshToggle] = useState(0);
  const triggerRefresh = useCallback(() => setRefreshToggle(prev => prev + 1), []);

  // AbortController ref for cancelling pending batch requests
  const batchesAbortControllerRef = useRef(null);
  // Request ID ref to prevent race conditions when agency changes rapidly
  const batchesRequestIdRef = useRef(0);
  // Same race guard for payment plans (loaded alongside batches)
  const paymentPlansRequestIdRef = useRef(0);

  // Plan balances ride on READ:AR, the existing "can see what I owe" permission.
  const { canAccessAR } = usePermissions();

  /**
   * Load the selected agency's active payment plans.
   *
   * Scoped to selectedAgencyId rather than the tab's sub-agency filter: the
   * endpoint validates agency access per call, and plans belong to the agency
   * the user is working in. Sub-agency plans are out of scope.
   */
  const loadPaymentPlans = useCallback(async () => {
    if (!selectedAgencyId || !canAccessAR) {
      setPaymentPlans([]);
      return;
    }

    const currentRequestId = ++paymentPlansRequestIdRef.current;

    try {
      const result = await getPaymentPlans(selectedAgencyId);

      // Ignore a response that a newer agency selection has superseded.
      if (currentRequestId !== paymentPlansRequestIdRef.current) return;

      setPaymentPlans(result.success ? (result.data || []) : []);
    } catch (err) {
      if (currentRequestId === paymentPlansRequestIdRef.current) {
        console.error('Failed to load payment plans:', err);
        setPaymentPlans([]);
      }
    }
  }, [selectedAgencyId, canAccessAR]);

  // Server-side pagination state for Active Reports
  const [activePagination, setActivePagination] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE.REPORTS,
    totalCount: 0
  });

  // Server-side pagination state for Recent Reports
  const [recentPagination, setRecentPagination] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE.REPORTS,
    totalCount: 0
  });

  // Search state
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [recentSearchTerm, setRecentSearchTerm] = useState('');

  // Date range filter for Recent Reports ('YYYY-MM-DD' strings; empty fromDate = unbounded lookback)
  const [recentDateRange, setRecentDateRange] = useState(getDefaultRecentDateRange);

  // Include sub-agencies filter
  const [includeSubAgencies, setIncludeSubAgencies] = useState(true);

  // PDF viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [submittedPdfData, setSubmittedPdfData] = useState(null);
  const [submittedReportNumber, setSubmittedReportNumber] = useState('');

  // Set default agency on mount - prefer main agency, then canPost, then first
  useEffect(() => {
    if (agencies.length > 0 && !selectedAgencyId) {
      // Find main agency first, then fall back to one with posting privileges, then first agency
      const mainAgency = agencies.find(a => a.isMainAgency);
      const canPostAgency = agencies.find(a => a.canPost);
      const defaultAgency = mainAgency || canPostAgency || agencies[0];
      setSelectedAgencyId(defaultAgency.agencyId);
    }
  }, [agencies, selectedAgencyId]);

  // Handle navigation state from report submission
  useEffect(() => {
    if (location.state?.showPdf) {
      setActiveTab(location.state.activeTab || 1);
      if (location.state.pdfData) {
        setSubmittedPdfData(location.state.pdfData);
        setSubmittedReportNumber(location.state.reportNumber || '');
        setPdfViewerOpen(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Keep the URL in sync with the active tab so a refresh restores it.
  // Default tab (index 0) drops the param entirely to keep URLs clean.
  useEffect(() => {
    const currentName = searchParams.get(TAB_PARAM);
    const desiredName = activeTab === 0 ? null : TAB_NAMES[activeTab];
    if (currentName === desiredName) return;
    const next = new URLSearchParams(searchParams);
    if (desiredName === null) {
      next.delete(TAB_PARAM);
    } else {
      next.set(TAB_PARAM, desiredName);
    }
    setSearchParams(next, { replace: true });
    // searchParams/setSearchParams are stable; depend only on activeTab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Handle closing PDF viewer
  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    setSubmittedPdfData(null);
    setSubmittedReportNumber('');
  };

  // Fetch functions for each tab - note: dependencies exclude pagination state to avoid circular updates
  const loadActiveReports = useCallback(async (page, pageSize, searchTerm) => {
    if (!selectedAgencyId) return;

    setIsLoadingActive(true);
    try {
      const result = await getActiveReports(selectedAgencyId, page + 1, pageSize, searchTerm);
      if (result.success && result.data) {
        setActiveReports(result.data.items || []);
        setActivePagination(prev => ({
          ...prev,
          page,
          pageSize,
          totalCount: result.data.totalCount || 0
        }));
      } else {
        console.error('Failed to fetch active reports:', result.error);
      }
    } catch (err) {
      console.error('Failed to fetch active reports:', err);
    } finally {
      setIsLoadingActive(false);
    }
  }, [selectedAgencyId]);

  const loadRecentReports = useCallback(async (page, pageSize, searchTerm, includeSubAgenciesParam, dateRange) => {
    if (!selectedAgencyId) return;

    setIsLoadingRecent(true);
    try {
      const result = await getRecentReports(selectedAgencyId, page + 1, pageSize, includeSubAgenciesParam, searchTerm, dateRange?.fromDate, dateRange?.toDate);
      if (result.success && result.data) {
        setRecentReports(result.data.items || []);
        setRecentPagination(prev => ({
          ...prev,
          page,
          pageSize,
          totalCount: result.data.totalCount || 0
        }));
      } else {
        console.error('Failed to fetch recent reports:', result.error);
      }
    } catch (err) {
      console.error('Failed to fetch recent reports:', err);
    } finally {
      setIsLoadingRecent(false);
    }
  }, [selectedAgencyId]);

  // Load all batches data (batches + agencies) using the combined endpoint
  // This is more efficient than calling getBatchesToPay and getAgenciesWithBatches separately
  // Uses AbortController and request ID pattern to prevent race conditions
  const loadBatchesData = useCallback(async () => {
    if (!selectedAgencyId) return;

    // Cancel any pending batch request
    if (batchesAbortControllerRef.current) {
      batchesAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    batchesAbortControllerRef.current = abortController;

    // Increment request ID to track this specific request
    const currentRequestId = ++batchesRequestIdRef.current;

    setIsLoadingBatches(true);
    setIsLoadingAgenciesWithBatches(true);
    try {
      const result = await getBatchesWithAgencies(selectedAgencyId);

      // Check if this request was aborted or a newer request was made
      if (abortController.signal.aborted || currentRequestId !== batchesRequestIdRef.current) {
        console.log('Batches request cancelled or superseded by newer request');
        return;
      }

      if (result.success && result.data) {
        setBatchesToPay(result.data.batches || []);
        setAgenciesWithBatches(result.data.agencies || []);
        // Default to main agency if it has batches, otherwise show all
        const mainWithBatches = result.data.agencies?.find(a => a.isMainAgency);
        setBatchAgencyFilter(mainWithBatches ? mainWithBatches.agencyId : null);
      } else {
        console.error('Failed to fetch batches data:', result.error);
      }
    } catch (err) {
      // Ignore abort errors - they're expected when cancelling requests
      if (err.name === 'AbortError') {
        console.log('Batches request aborted');
        return;
      }
      console.error('Failed to fetch batches data:', err);
    } finally {
      // Only update loading states if this is still the current request
      if (currentRequestId === batchesRequestIdRef.current) {
        setIsLoadingBatches(false);
        setIsLoadingAgenciesWithBatches(false);
        setBatchesDataLoaded(true);
      }
    }
  }, [selectedAgencyId]);

  // Handle agency filter change in Batches to Pay tab
  const handleBatchAgencyFilterChange = useCallback((agencyId) => {
    // Close payment modal if open to prevent agency mismatch
    setBatchPaymentModalOpen(false);
    setBatchAgencyFilter(agencyId);
  }, []);

  // Load all reports (initial load - resets to page 0)
  // Batches load in background - don't block page render
  const loadAllReports = useCallback(async () => {
    if (!selectedAgencyId) return;

    try {
      // Load Active and Recent tabs in parallel - reset to page 0
      // These are fast and should complete quickly
      const defaultRange = getDefaultRecentDateRange();
      await Promise.all([
        loadActiveReports(0, DEFAULT_PAGE_SIZE.REPORTS, ''),
        loadRecentReports(0, DEFAULT_PAGE_SIZE.REPORTS, '', true, defaultRange)
      ]);
      // Reset search terms and date range
      setActiveSearchTerm('');
      setRecentSearchTerm('');
      setRecentDateRange(defaultRange);
    } catch (err) {
      console.error('Failed to fetch power reports:', err);
      toast.error('Failed to load power reports');
    }

    // Load batches in background (don't await - let it run async)
    // This way page is usable immediately while batches load
    loadBatchesData().catch(err => {
      console.error('Failed to load batches in background:', err);
    });

    loadPaymentPlans().catch(err => {
      console.error('Failed to load payment plans in background:', err);
    });
  }, [selectedAgencyId, loadActiveReports, loadRecentReports, loadBatchesData, loadPaymentPlans]);

  // Pagination handlers - these don't reset page, they use the new page from the event
  const handleActivePaginationChange = useCallback((paginationModel) => {
    loadActiveReports(paginationModel.page, paginationModel.pageSize, activeSearchTerm);
  }, [loadActiveReports, activeSearchTerm]);

  const handleRecentPaginationChange = useCallback((paginationModel) => {
    loadRecentReports(paginationModel.page, paginationModel.pageSize, recentSearchTerm, includeSubAgencies, recentDateRange);
  }, [loadRecentReports, recentSearchTerm, includeSubAgencies, recentDateRange]);

  // Search handlers - reset to page 0 when searching
  const handleActiveSearchChange = useCallback((searchTerm) => {
    setActiveSearchTerm(searchTerm);
    loadActiveReports(0, activePagination.pageSize, searchTerm);
  }, [loadActiveReports, activePagination.pageSize]);

  const handleRecentSearchChange = useCallback((searchTerm) => {
    setRecentSearchTerm(searchTerm);
    loadRecentReports(0, recentPagination.pageSize, searchTerm, includeSubAgencies, recentDateRange);
  }, [loadRecentReports, recentPagination.pageSize, includeSubAgencies, recentDateRange]);

  // Date range handler - reset to page 0 when the range changes
  const handleRecentDateRangeChange = useCallback((dateRange) => {
    setRecentDateRange(dateRange);
    loadRecentReports(0, recentPagination.pageSize, recentSearchTerm, includeSubAgencies, dateRange);
  }, [loadRecentReports, recentPagination.pageSize, recentSearchTerm, includeSubAgencies]);

  // Batch payment handlers
  const handleBatchPaymentSubmit = useCallback((selectedBatches, paymentAmounts) => {
    setSelectedBatchesForPayment(selectedBatches);
    setPaymentAmountsForPayment(paymentAmounts);
    setBatchPaymentModalOpen(true);
  }, []);

  const handleBatchPaymentSuccess = useCallback((response) => {
    if (response.pdfBase64) {
      setSubmittedPdfData(response.pdfBase64);
      setSubmittedReportNumber(response.receiptNumber || 'Batch Payment Receipt');
      setPdfViewerOpen(true);
    }
    // Forget the paid selection so the modal can't reopen with it (Bug 4430)
    setSelectedBatchesForPayment(new Set());
    setPaymentAmountsForPayment({});
    // triggerRefresh now refreshes all tabs (active, recent, and batches)
    triggerRefresh();
    setBatchesTabKey(prev => prev + 1);
  }, [triggerRefresh]);

  // A rejected payment usually means the list is stale (the batches were paid from another
  // tab), so reload Batches to Pay once the modal closes (Bug 4430). Not while it is open:
  // a reload resets the agency filter the modal's agency comes from.
  const batchesStaleRef = useRef(false);

  const handleBatchPaymentFailure = useCallback(() => {
    batchesStaleRef.current = true;
  }, []);

  const handleBatchPaymentModalClose = useCallback(() => {
    setBatchPaymentModalOpen(false);
    if (batchesStaleRef.current) {
      batchesStaleRef.current = false;
      setSelectedBatchesForPayment(new Set());
      setPaymentAmountsForPayment({});
      loadBatchesData();
    }
  }, [loadBatchesData]);


  // Cleanup: abort pending batch requests on unmount
  useEffect(() => {
    return () => {
      if (batchesAbortControllerRef.current) {
        batchesAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Reset batchesDataLoaded when agency changes to force reload
  useEffect(() => {
    setBatchesDataLoaded(false);
  }, [selectedAgencyId]);

  // Fetch all reports when agency changes (initial load - resets pagination)
  useEffect(() => {
    if (selectedAgencyId) {
      loadAllReports();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgencyId]); // Only re-run when agency changes, not on every loadAllReports change

  // Fallback: load batches if not already loaded when tab is selected
  // (Batches start loading on page load in background, this is a safety net)
  useEffect(() => {
    if (activeTab === 2 && selectedAgencyId && !batchesDataLoaded && !isLoadingBatches && !isLoadingAgenciesWithBatches) {
      loadBatchesData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedAgencyId, batchesDataLoaded]); // Load when tab becomes active if not already loading

  // Handle manual refresh (triggered by triggerRefresh)
  useEffect(() => {
    if (refreshToggle > 0 && selectedAgencyId) {
      // Refresh all tabs to ensure consistency after mutations (void, delete, create, etc.)
      loadActiveReports(activePagination.page, activePagination.pageSize, activeSearchTerm);
      loadRecentReports(recentPagination.page, recentPagination.pageSize, recentSearchTerm, includeSubAgencies, recentDateRange);
      loadBatchesData();
      // Voiding an ACH removes its plan payment, so plans must refresh too.
      loadPaymentPlans();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToggle]); // Only refresh on manual trigger

  // Refresh recent reports when includeSubAgencies changes
  useEffect(() => {
    if (selectedAgencyId && activeTab === 1) {
      loadRecentReports(0, recentPagination.pageSize, recentSearchTerm, includeSubAgencies, recentDateRange);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeSubAgencies]);

  // Loading state
  if (authLoading) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <OathDualSpinner size="lg" className="mx-auto" />
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading Power Reporting...</p>
          </div>
        </div>
      </OathPageWrapper>
    );
  }

  // No agencies
  if (!agencies.length) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <OathContentCard>
          <OathEmptyState
            title="No Agency Access"
            message="You do not have access to any agencies for Power Reporting."
          />
        </OathContentCard>
      </OathPageWrapper>
    );
  }

  return (
    <OathPageWrapper maxWidth="max-w-7xl">
      {/* Header Card */}
      <OathHeaderCard
        title="Power Reporting"
        subtitle="Report executed powers, view premium calculations, and submit reports"
        icon={DocumentTextIcon}
        rightContent={
          <OathButton
            onClick={() => setCreateModalOpen(true)}
            icon={PlusIcon}
            className="w-full sm:w-auto"
          >
            Create Report
          </OathButton>
        }
      />

      {/* Main Content Card with Tabs */}
      <OathContentCard noPadding>
        <TabGroup selectedIndex={activeTab} onChange={setActiveTab}>
          <TabList className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <Tab
              className={({ selected }) =>
                `flex-1 py-4 px-2 text-center text-xs sm:text-sm font-medium transition-colors focus:outline-none ${
                  selected
                    ? 'bg-white dark:bg-gray-800 text-primary dark:text-primary-light font-semibold border-b-2 border-primary'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <div className="flex flex-col items-center gap-1">
                <ClipboardDocumentListIcon className="h-5 w-5 hidden sm:block" />
                <span className="hidden sm:inline">Active Reports</span>
                <span className="sm:hidden">Active</span>
                {isLoadingActive
                  ? <span className="inline-block w-6 h-4 bg-gray-200 dark:bg-gray-600 rounded-full animate-pulse" />
                  : <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1.5 text-[10px] font-bold rounded-full ${activePagination.totalCount > 0 ? 'bg-primary/15 dark:bg-primary/25 text-primary dark:text-primary-light' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>{activePagination.totalCount}</span>
                }
              </div>
            </Tab>
            <Tab
              className={({ selected }) =>
                `flex-1 py-4 px-2 text-center text-xs sm:text-sm font-medium transition-colors focus:outline-none ${
                  selected
                    ? 'bg-white dark:bg-gray-800 text-primary dark:text-primary-light font-semibold border-b-2 border-primary'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <div className="flex flex-col items-center gap-1">
                <ClockIcon className="h-5 w-5 hidden sm:block" />
                <span className="hidden sm:inline">Recent Reports</span>
                <span className="sm:hidden">Recent</span>
                {isLoadingRecent
                  ? <span className="inline-block w-6 h-4 bg-gray-200 dark:bg-gray-600 rounded-full animate-pulse" />
                  : <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1.5 text-[10px] font-bold rounded-full ${recentPagination.totalCount > 0 ? 'bg-primary/15 dark:bg-primary/25 text-primary dark:text-primary-light' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>{recentPagination.totalCount}</span>
                }
              </div>
            </Tab>
            <Tab
              className={({ selected }) =>
                `flex-1 py-4 px-2 text-center text-xs sm:text-sm font-medium transition-colors focus:outline-none ${
                  selected
                    ? 'bg-white dark:bg-gray-800 text-primary dark:text-primary-light font-semibold border-b-2 border-primary'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <div className="flex flex-col items-center gap-1">
                <CreditCardIcon className="h-5 w-5 hidden sm:block" />
                <span className="hidden sm:inline">Batches to Pay</span>
                <span className="sm:hidden">To Pay</span>
                {isLoadingBatches || isLoadingAgenciesWithBatches
                  ? <span className="inline-block w-6 h-4 bg-gray-200 dark:bg-gray-600 rounded-full animate-pulse" />
                  : <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1.5 text-[10px] font-bold rounded-full ${batchesToPay.length > 0 ? 'bg-primary/15 dark:bg-primary/25 text-primary dark:text-primary-light' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>{batchesToPay.length}</span>
                }
              </div>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Active Reports Tab */}
            <TabPanel className="p-4 sm:p-6">
              <ActiveReportsTab
                reports={activeReports}
                isLoading={isLoadingActive}
                onRefresh={triggerRefresh}
                pagination={activePagination}
                onPaginationChange={handleActivePaginationChange}
                searchTerm={activeSearchTerm}
                onSearchChange={handleActiveSearchChange}
                agencyId={selectedAgencyId}
              />
            </TabPanel>

            {/* Recent Reports Tab */}
            <TabPanel className="p-4 sm:p-6">
              <RecentReportsTab
                reports={recentReports}
                isLoading={isLoadingRecent}
                onRefresh={triggerRefresh}
                pagination={recentPagination}
                onPaginationChange={handleRecentPaginationChange}
                searchTerm={recentSearchTerm}
                onSearchChange={handleRecentSearchChange}
                dateRange={recentDateRange}
                onDateRangeChange={handleRecentDateRangeChange}
                includeSubAgencies={includeSubAgencies}
                onIncludeSubAgenciesChange={setIncludeSubAgencies}
                agencyId={selectedAgencyId}
              />
            </TabPanel>

            {/* Batches to Pay Tab */}
            <TabPanel className="p-4 sm:p-6">
              <BatchesToPayTab
                key={batchesTabKey}
                batches={batchesToPay}
                isLoading={isLoadingBatches || isLoadingAgenciesWithBatches}
                onPaymentSubmit={handleBatchPaymentSubmit}
                agencies={agenciesWithBatches}
                selectedFilterAgencyId={batchAgencyFilter}
                onAgencyFilterChange={handleBatchAgencyFilterChange}
                paymentPlans={paymentPlans}
                // Voiding restores the collection batch's open BUF as well as the plan
                // balance, so refresh everything — not just the plans.
                onPaymentPlanVoided={triggerRefresh}
              />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </OathContentCard>

      {/* Create Report Modal */}
      <CreateReportModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={triggerRefresh}
        agencyId={selectedAgencyId}
        agencies={agencies}
      />

      {/* Batch Payment Modal */}
      <BatchPaymentModal
        isOpen={batchPaymentModalOpen}
        onClose={handleBatchPaymentModalClose}
        selectedBatches={selectedBatchesForPayment}
        paymentAmounts={paymentAmountsForPayment}
        batches={batchAgencyFilter ? batchesToPay.filter(b => b.agencyId === batchAgencyFilter) : batchesToPay}
        agencyId={batchPaymentAgencyId}
        onSuccess={handleBatchPaymentSuccess}
        onFailure={handleBatchPaymentFailure}
      />

      {/* PDF Viewer Modal */}
      <PdfViewerModal
        isOpen={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        pdfData={submittedPdfData}
        reportNumber={submittedReportNumber}
        title={`Submitted Report: ${submittedReportNumber}`}
      />
    </OathPageWrapper>
  );
};

export default withErrorBoundary(PowerReportingPage, {
  title: 'Power Reporting Error',
  message: 'An error occurred in the Power Reporting module. Please try again.'
});
